import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export interface AppVersionState {
  platform: string;
  currentVersion: string;
  latestVersion: string;
  minimumRequiredVersion: string;
  isUpdateAvailable: boolean;
  isForceUpdate: boolean;
  /** When true the mandatory-update blocking modal is shown and cannot be dismissed. */
  isMandatoryUpdate: boolean;
  updateUrl: string;
  releaseNotes: string;
  appSize: string;
  newFeatures: string[];
  message: string;
  errors: string;
  softUpdateDismissed: boolean;
}

const initialState: AppVersionState = {
  platform: '',
  currentVersion: '',
  latestVersion: '',
  minimumRequiredVersion: '',
  isUpdateAvailable: false,
  isForceUpdate: false,
  isMandatoryUpdate: false,
  updateUrl: '',
  releaseNotes: '',
  appSize: '',
  newFeatures: [],
  message: '',
  errors: '',
  softUpdateDismissed: false,
};

const appVersionSlice = createSlice({
  name: 'appVersion',
  initialState,
  reducers: {
    setVersionCheckResult: (
      state,
      action: PayloadAction<Omit<AppVersionState, 'softUpdateDismissed'>>,
    ) => {
      state.platform = action.payload.platform;
      state.currentVersion = action.payload.currentVersion;
      state.latestVersion = action.payload.latestVersion;
      state.minimumRequiredVersion = action.payload.minimumRequiredVersion;
      state.isUpdateAvailable = action.payload.isUpdateAvailable;
      state.isForceUpdate = action.payload.isForceUpdate;
      state.isMandatoryUpdate =
        state.isMandatoryUpdate && action.payload.isUpdateAvailable
          ? true
          : action.payload.isMandatoryUpdate;
      state.updateUrl = action.payload.updateUrl;
      state.releaseNotes = action.payload.releaseNotes;
      state.appSize = action.payload.appSize;
      state.newFeatures = action.payload.newFeatures;
      state.message = action.payload.message;
      state.errors = action.payload.errors;
      // Reset the dismissed flag on a fresh version-check so the banner
      // re-appears if the user previously dismissed and the app resumed.
      // For mandatory updates softUpdateDismissed has no effect on visibility
      // (the banner cannot be dismissed), so resetting it is harmless.
      state.softUpdateDismissed = false;
    },
    dismissSoftUpdate: state => {
      state.softUpdateDismissed = true;
    },
  },
});

export const { setVersionCheckResult, dismissSoftUpdate } =
  appVersionSlice.actions;
export default appVersionSlice.reducer;